import logger from '../lib/logger';
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Youtube, Facebook, Instagram, MessageCircle, Loader2 } from 'lucide-react';
import { socialLinks } from '../data/mock';
import { fetchPrayerTimes } from '../services/api';

const LOGO_URL = "/images/logo.png";

const Header = ({ shahadatMode, shahadatMessage }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [prayerData, setPrayerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const loadPrayerTimes = async () => {
      try {
        const data = await fetchPrayerTimes();
        setPrayerData(data);
      } catch (error) {
        logger.error('Failed to fetch prayer times:', error);
        // Use fallback data
        setPrayerData({
          fajr: '5:30 AM',
          sunrise: '6:45 AM',
          dhuhr: '12:30 PM',
          sunset: '5:15 PM',
          maghrib: '5:35 PM',
          hijri_date: 'Loading...',
          gregorian_date: new Date().toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })
        });
      } finally {
        setLoading(false);
      }
    };

    loadPrayerTimes();
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Madrassah', path: '/madrassah' },
    { name: 'Youth', path: '/youth-program' },
    { name: 'Membership', path: '/membership' },
    { name: 'Donate', path: '/donate' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50">
      {/* Shahadat/Mourning Strip — shown when admin enables it */}
      {shahadatMode && (
        <div className="bg-[#0a0000] text-center py-1.5 border-b border-[#5c1a1a]/30" data-testid="shahadat-strip">
          <p className="text-[#c27a7a]/80 text-[10px] sm:text-xs tracking-[0.3em] uppercase">
            {shahadatMessage || 'Inna Lillahi Wa Inna Ilayhi Rajioon · إنا لله و إنا إليه راجعون'}
          </p>
        </div>
      )}

      {/* Elegant Top Bar */}
      <div className="bg-[#0f1a14] text-white py-2 sm:py-3 px-4 border-b border-[#c5a572]/20">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-4">
            {loading ? (
              <Loader2 className="w-4 h-4 text-[#c5a572] animate-spin" />
            ) : (
              <>
                <span className="text-[#c5a572] text-xs sm:text-sm tracking-wider">{prayerData?.hijri_date}</span>
                <span className="text-[#c5a572]/40 hidden sm:inline">|</span>
                <span className="text-white/60 text-xs sm:text-sm tracking-wide">{prayerData?.gregorian_date}</span>
              </>
            )}
          </div>
          
          {/* Social Links & Watch Live */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <a 
                href={socialLinks.facebook}
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8 h-8 border border-[#c5a572]/30 hover:border-[#c5a572] hover:bg-[#c5a572]/10 flex items-center justify-center transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4 text-[#c5a572]" />
              </a>
              <a 
                href={socialLinks.instagram}
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8 h-8 border border-[#c5a572]/30 hover:border-[#c5a572] hover:bg-[#c5a572]/10 flex items-center justify-center transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4 text-[#c5a572]" />
              </a>
              <a 
                href={socialLinks.whatsapp}
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8 h-8 border border-[#c5a572]/30 hover:border-[#c5a572] hover:bg-[#c5a572]/10 flex items-center justify-center transition-all duration-300"
                aria-label="WhatsApp"
              >
                <MessageCircle className="w-4 h-4 text-[#c5a572]" />
              </a>
            </div>
            
            <a 
              href={socialLinks.youtube}
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-[#c5a572] hover:bg-[#d4bb8a] text-[#0f1a14] text-xs uppercase tracking-widest transition-all duration-300"
            >
              <Youtube className="w-4 h-4" />
              <span className="hidden sm:inline">Watch Live</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white/95 backdrop-blur-md shadow-lg border-b border-[#c5a572]/10">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 min-w-0">
              <img 
                src={LOGO_URL} 
                alt="Fatimia Community Services" 
                className="h-12 sm:h-16 w-auto flex-shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-base sm:text-2xl text-[#1a2f23] tracking-wide sm:tracking-widest truncate">Fatimia Community</h1>
                <p className="text-[10px] sm:text-xs text-[#c5a572] uppercase tracking-wider sm:tracking-[0.25em]">Services Society of BC</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`nav-link ${
                    isActive(item.path) ? 'active' : ''
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 border border-[#c5a572]/30 hover:border-[#c5a572] transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-[#1a2f23]" />
              ) : (
                <Menu className="w-6 h-6 text-[#1a2f23]" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-[#c5a572]/20 py-6">
            <div className="container mx-auto px-4 flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`py-4 px-6 text-sm uppercase tracking-widest transition-all ${
                    isActive(item.path)
                      ? 'bg-[#c5a572]/10 text-[#c5a572] border-l-4 border-[#c5a572]'
                      : 'text-[#5a6d62] hover:bg-[#f8f6f1] hover:text-[#c5a572]'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Elegant Prayer Times Bar */}
      <div className="prayer-bar">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-6 sm:gap-12 flex-wrap py-1">
            {loading ? (
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading prayer times...
              </div>
            ) : (
              <>
                <PrayerTime name="Fajr" time={prayerData?.fajr} />
                <Divider />
                <PrayerTime name="Sunrise" time={prayerData?.sunrise} />
                <Divider />
                <PrayerTime name="Dhuhr" time={prayerData?.dhuhr} />
                <Divider />
                <PrayerTime name="Sunset" time={prayerData?.sunset} />
                <Divider />
                <PrayerTime name="Maghrib" time={prayerData?.maghrib} />
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

const PrayerTime = ({ name, time }) => (
  <div className="flex items-center gap-3 text-sm">
    <span className="text-[#c5a572] uppercase tracking-[0.2em] text-xs">{name}</span>
    <span className="text-white text-base tracking-wider">{time}</span>
  </div>
);

const Divider = () => (
  <span className="hidden sm:block w-1 h-1 bg-[#c5a572]/40 rounded-full" />
);

export default Header;
